import { ReebeloStoreT } from '@lambda/reebelo';
import { LineItemType } from '@lambda/apis/src/checkout/enum';
import {
  ADJUSTMENT_FACTORS,
  BUYER_FEE_ADJUSTMENT_FACTORS,
  CATEGORY_DEFAULT_ADJUSTMENT_FACTORS,
  CATEGORY_NAME_ALIASES,
  GLOBAL_DEFAULT_ADJUSTMENT_FACTOR,
  GLOBAL_DEFAULT_BUYER_FEE_ADJUSTMENT_FACTOR,
  STORE_DEFAULT_ADJUSTMENT_FACTORS,
} from './data';

// Only PRODUCT lines carry the catalog category layer (categoryName);
// warranty/shipping lines aren't tied to a catalog category (the backend
// reports "Unknown" for those), so they resolve to a fixed label instead.
const LINE_ITEM_TYPE_LABELS: Partial<Record<LineItemType, string>> = {
  [LineItemType.WARRANTY]: 'Warranty',
  [LineItemType.SHIPPING]: 'Shipping',
};

const CATEGORY_NAME_ALIASES_BY_LOWERCASE: Record<string, string> =
  Object.fromEntries(
    Object.entries(CATEGORY_NAME_ALIASES).map(([alias, english]) => [
      alias.toLowerCase(),
      english,
    ]),
  );

// Translated catalog category names are normalised to English (RT-17393).
export const getLineItemCategory = (
  lineItemType: LineItemType,
  categoryName?: string,
): string | undefined =>
  lineItemType === LineItemType.PRODUCT
    ? (categoryName &&
        CATEGORY_NAME_ALIASES_BY_LOWERCASE[
          categoryName.trim().toLowerCase()
        ]) ||
      categoryName
    : LINE_ITEM_TYPE_LABELS[lineItemType];

// Resolution order (see data.ts): exact store+category match, then the
// store's default, then the category's default, then the global default.
// A `null` factor in ADJUSTMENT_FACTORS is treated as missing.
export const getAdjustmentFactor = (
  store: ReebeloStoreT,
  lineItemType: LineItemType,
  categoryName?: string,
): number => {
  const category = getLineItemCategory(lineItemType, categoryName);
  const specific = category
    ? ADJUSTMENT_FACTORS[store]?.[category]
    : undefined;

  if (specific != null) {
    return specific;
  }

  const storeDefault = STORE_DEFAULT_ADJUSTMENT_FACTORS[store];

  if (storeDefault != null) {
    return storeDefault;
  }

  const categoryDefault = category
    ? CATEGORY_DEFAULT_ADJUSTMENT_FACTORS[category]
    : undefined;

  if (categoryDefault != null) {
    return categoryDefault;
  }

  return GLOBAL_DEFAULT_ADJUSTMENT_FACTOR;
};

export const getBuyerFeeAdjustmentFactor = (store: ReebeloStoreT): number =>
  BUYER_FEE_ADJUSTMENT_FACTORS[store] ??
  GLOBAL_DEFAULT_BUYER_FEE_ADJUSTMENT_FACTOR;
